import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Shield,
  Lock,
  BadgeCheck,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { SeoHead } from "../../../seo/SeoHead";
import {
  buildBreadcrumbListJsonLd,
  buildFAQPageJsonLd,
} from "../../../seo/site";

export function SecteurTemplate({
  metier,
  title,
  description,
  heroTitle,
  heroSubtitle,
  painPoints,
  solutions,
  useCases,
  faq,
  slug,
}) {
  const [openFaq, setOpenFaq] = useState(null);
  return (
    <div className="bg-stone-50 font-body text-[#010101] selection:bg-[#A43032] selection:text-white min-h-screen">
      <SeoHead
        title={title}
        description={description}
        jsonLd={[
          buildBreadcrumbListJsonLd([
            { name: "Accueil", path: "/" },
            { name: "Secteurs", path: "/secteurs" },
            { name: metier, path: `/secteurs/${slug}` },
          ]),
          ...(faq ? [buildFAQPageJsonLd(faq)] : []),
        ]}
      />

      {/* HERO SECTION */}
      <section className="relative px-4 pt-16 pb-12 sm:px-6 lg:px-8 max-w-7xl mx-auto flex flex-col items-center text-center overflow-hidden">
        <div className="absolute top-20 left-10 size-72 bg-[#A43032]/5 rounded-full blur-3xl" />
        <div className="absolute top-40 right-10 size-96 bg-[#A43032]/10 rounded-full blur-3xl" />

        <div className="relative inline-flex items-center gap-2 rounded-full border-2 border-[#A43032]/30 bg-[#A43032]/10 px-4 py-2 text-sm font-semibold text-[#A43032] mb-8 backdrop-blur-sm">
          <span>🎯</span> Spécial {metier}
        </div>

        <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-[#010101] max-w-4xl leading-[1.1] relative z-10">
          {heroTitle}
        </h1>

        <p className="mt-8 text-lg md:text-xl text-stone-600 max-w-2xl font-medium relative z-10">
          {heroSubtitle}
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 w-full max-w-md relative z-10">
          <Link
            to="/contact"
            className="w-full sm:w-auto inline-flex justify-center items-center rounded-2xl bg-[#A43032] px-8 py-4 text-base font-bold text-white shadow-xl transition-all hover:scale-105 hover:bg-[#8a2729]"
          >
            Demander une démo <ArrowRight className="ml-2 size-5" />
          </Link>
        </div>

        <div className="flex flex-wrap gap-3 items-center justify-center mt-12 max-w-4xl mx-auto relative z-10">
          <span className="inline-flex items-center gap-2 rounded-xl bg-white border border-stone-200 px-4 py-2 text-sm font-semibold text-stone-700">
            <Shield className="size-4 text-[#A43032]" />
            <span>Sécurité assurée</span>
          </span>
          <span className="inline-flex items-center gap-2 rounded-xl bg-white border border-stone-200 px-4 py-2 text-sm font-semibold text-stone-700">
            <Lock className="size-4 text-[#A43032]" />
            <span>Hébergé en France</span>
          </span>
          <span className="inline-flex items-center gap-2 rounded-xl bg-white border border-stone-200 px-4 py-2 text-sm font-semibold text-stone-700">
            <BadgeCheck className="size-4 text-[#A43032]" />
            <span>Conforme RGPD</span>
          </span>
        </div>
      </section>

      {/* PROBLÈMES VS SOLUTIONS */}
      <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-stone-200">
        <h2 className="font-heading text-3xl font-semibold text-center mb-12">
          Pourquoi Raguia pour les {metier} ?
        </h2>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className="bg-white p-8 rounded-2xl border-2 border-stone-200 shadow-sm">
            <h3 className="font-heading text-xl font-semibold text-red-600 mb-6 flex items-center gap-2">
              <span className="size-8 rounded-full bg-red-100 flex items-center justify-center">
                ✗
              </span>
              Vos défis actuels
            </h3>
            <ul className="space-y-4">
              {painPoints.map((point, i) => (
                <li key={i} className="flex gap-3 text-stone-600">
                  <span className="text-red-500 mt-1 flex-shrink-0">✗</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-gradient-to-br from-[#010101] to-stone-900 p-8 rounded-2xl border border-stone-800 shadow-xl text-white">
            <h3 className="font-heading text-xl font-semibold text-[#A43032] mb-6 flex items-center gap-2">
              <span className="size-8 rounded-full bg-[#A43032]/20 flex items-center justify-center">
                ✓
              </span>
              La solution Raguia
            </h3>
            <ul className="space-y-4">
              {solutions.map((sol, i) => (
                <li key={i} className="flex gap-3 text-white/90">
                  <CheckCircle2 className="size-5 text-[#A43032] mt-0.5 flex-shrink-0" />
                  <span>{sol}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CAS D'USAGE */}
      <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-stone-200 bg-[#fcfafb]">
        <h2 className="font-heading text-3xl font-semibold text-center mb-12">
          Cas d'usage concrets
        </h2>
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {useCases.map((uc) => (
            <div
              key={uc.title}
              className="bg-white p-6 rounded-2xl border-2 border-stone-200 shadow-md"
            >
              <h3 className="font-heading font-semibold text-lg text-[#A43032] mb-3">
                {uc.title}
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                {uc.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ SECTION */}
      {faq && faq.length > 0 && (
        <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl font-semibold text-[#010101] mb-3">
              Questions fréquentes
            </h2>
            <p className="text-stone-600 font-medium">
              Tout ce que les {metier} nous demandent
            </p>
          </div>
          <div className="space-y-3">
            {faq.map((item, i) => (
              <div
                key={item.q}
                className="bg-white rounded-2xl border-2 border-stone-200 overflow-hidden hover:border-[#A43032]/30 transition-all shadow-sm"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left font-bold text-[#010101] hover:bg-stone-50 transition-colors"
                  aria-expanded={openFaq === i}
                >
                  <span>{item.q}</span>
                  {openFaq === i ? (
                    <ChevronUp className="size-5 text-[#A43032] shrink-0" />
                  ) : (
                    <ChevronDown className="size-5 text-stone-400 shrink-0" />
                  )}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-stone-600 font-medium leading-relaxed border-t border-stone-100">
                    <p className="pt-4">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA FINAL */}
      <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center">
        <div className="bg-gradient-to-r from-[#A43032] to-[#8a2729] rounded-3xl p-10 text-white shadow-2xl">
          <h2 className="font-heading text-3xl font-semibold mb-4">
            Prêt à transformer votre activité ?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Découvrez comment Raguia s'adapte spécifiquement aux besoins des{" "}
            {metier}. Installation clé en main en 24h.
          </p>
          <Link
            to="/contact"
            className="inline-flex rounded-full bg-white px-8 py-4 text-base font-bold text-[#A43032] transition hover:scale-105"
          >
            Obtenir mon devis
          </Link>
        </div>
      </section>
    </div>
  );
}
